import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { ClientCombobox, type ComboClient } from "../../components/ClientCombobox";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { useEditDialog } from "../../components/EditDialog";
import { PATHS } from "../../app/paths";

type Client = ComboClient;

type BatchStatus = "Uploading" | "Parsing" | "Review" | "Committing" | "Committed" | "Failed" | "Discarded";

interface SpendBatch {
  batchGuid: string;
  clientId: number;
  clientName: string;
  status: BatchStatus;
  statusDetail: string | null;
  fileName: string;
  fileSizeBytes: number;
  uploadedUtc: string;
  committedUtc: string | null;
  createdByAppUserId: number;
}

interface ClientSpendRecord {
  clientSpendId: number;
  supplierId: number | null;
  supplierNameRaw: string;
  gradeId: number | null;
  tradeNameRaw: string;
  invoiceDate: string;
  quantity: number;
  quantityUomCode: string;
  unitPrice: number;
  priceUomCode: string;
  currencyCode: string;
  pricePerLbUsd: number | null;
  supplierExternalId: string | null;
  poLineNumber: string | null;
  poNumber: string | null;
  materialName: string | null;
  materialNumber: string | null;
  receivingLocationRaw: string | null;
  receivingCity: string | null;
  receivingRegionCode: string | null;
  purchaseTypeCode: string | null;
  paymentTerms: string | null;
  priceIndexBasis: string | null;
  rebates: string | null;
  businessUnit: string | null;
  notes: string | null;
  batchGuid: string;
  createdByAppUserId: number;
  committedUtc: string;
}

const STATUS_CHIP: Record<BatchStatus, string> = {
  Uploading: "info",
  Parsing: "info",
  Review: "info",
  Committing: "info",
  Committed: "ok",
  Failed: "bad",
  Discarded: "bad",
};

const REVIEWABLE_STATUSES: BatchStatus[] = ["Uploading", "Parsing", "Review", "Failed"];

const CLIENT_STATUSES = ["Active", "Prospect", "Inactive", "Merged"] as const;

// Mirrors the "Spend Data" tab of the intake workbook column-for-column, so
// a reviewer can check a committed record against the original file without
// translating field names. sortKey is only set for columns the API can sort.
const COLUMNS: { label: string; render: (r: ClientSpendRecord) => string; sortKey?: string }[] = [
  { label: "Vendor/Supplier Name", render: (r) => r.supplierNameRaw, sortKey: "supplierNameRaw" },
  { label: "Trade Name", render: (r) => r.tradeNameRaw, sortKey: "tradeNameRaw" },
  { label: "Date", render: (r) => r.invoiceDate, sortKey: "invoiceDate" },
  { label: "Quantity Ordered", render: (r) => r.quantity.toLocaleString(), sortKey: "quantity" },
  { label: "Price", render: (r) => r.unitPrice.toFixed(4), sortKey: "unitPrice" },
  { label: "Price UoM", render: (r) => r.priceUomCode },
  { label: "Currency", render: (r) => r.currencyCode },
  { label: "Vendor/Supplier ID", render: (r) => r.supplierExternalId ?? "—" },
  { label: "PO Line #", render: (r) => r.poLineNumber ?? "—" },
  { label: "PO Number", render: (r) => r.poNumber ?? "—" },
  { label: "Material Name", render: (r) => r.materialName ?? "—" },
  { label: "Material Number", render: (r) => r.materialNumber ?? "—" },
  { label: "Receiving Location", render: (r) => r.receivingLocationRaw ?? "—" },
  { label: "Purchase Type", render: (r) => r.purchaseTypeCode ?? "—" },
  { label: "Payment Terms", render: (r) => r.paymentTerms ?? "—" },
  { label: "Price Index / Basis", render: (r) => r.priceIndexBasis ?? "—" },
  { label: "Rebates", render: (r) => r.rebates ?? "—" },
  { label: "Business Unit / Subsidiary", render: (r) => r.businessUnit ?? "—" },
  { label: "Notes", render: (r) => r.notes ?? "—" },
];

const PAGE_SIZE = 50;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ClientsTab() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { appRole, appUserId } = useAppUser();
  const canManage = (createdByAppUserId: number) =>
    appRole === "Admin" || (appRole === "Uploader" && createdByAppUserId === appUserId);
  const { confirm, dialog } = useConfirmDialog();
  const { edit: editFields, dialog: editDialog } = useEditDialog();

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState(false);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editStatus, setEditStatus] = useState<string>("Active");
  const [savingClient, setSavingClient] = useState(false);

  const [batches, setBatches] = useState<SpendBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const [records, setRecords] = useState<ClientSpendRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("invoiceDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [offset, setOffset] = useState(0);

  const loadBatches = (clientId: number) => {
    setBatchesLoading(true);
    apiFetch(`clients/${clientId}/spend-batches`)
      .then((res) => res.json())
      .then(setBatches)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setBatchesLoading(false));
  };

  const loadRecords = (clientId: number) => {
    setRecordsLoading(true);
    const params = new URLSearchParams({
      sortBy,
      sortDir,
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (supplierFilter.trim()) params.set("supplier", supplierFilter.trim());
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    apiFetch(`clients/${clientId}/spend?${params.toString()}`)
      .then((res) => res.json())
      .then((page: { items: ClientSpendRecord[]; totalCount: number }) => {
        setRecords(page.items);
        setTotalCount(page.totalCount);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setRecordsLoading(false));
  };

  useEffect(() => {
    if (!selectedClient) return;
    setError(null);
    loadBatches(selectedClient.clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    setError(null);
    loadRecords(selectedClient.clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, sortBy, sortDir, supplierFilter, dateFrom, dateTo, offset]);

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setOffset(0);
  };

  const downloadFile = async (batchGuid: string) => {
    try {
      const res = await apiFetch(`spend/batches/${batchGuid}/file`);
      const file: { readUrl: string | null } = await res.json();
      if (file.readUrl) window.open(file.readUrl, "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const startEditClient = () => {
    if (!selectedClient) return;
    setEditName(selectedClient.clientName);
    setEditCode(selectedClient.clientCode ?? "");
    setEditStatus(selectedClient.status);
    setEditingClient(true);
  };

  const saveClient = async () => {
    if (!selectedClient) return;
    setSavingClient(true);
    setError(null);
    try {
      const res = await apiFetch(`clients/${selectedClient.clientId}`, {
        method: "PATCH",
        body: JSON.stringify({
          clientName: editName.trim(),
          clientCode: editCode.trim() || null,
          status: editStatus,
        }),
      });
      const updated: Client = await res.json();
      setSelectedClient(updated);
      setEditingClient(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSavingClient(false);
    }
  };

  const deleteClient = async () => {
    if (!selectedClient) return;
    const result = await confirm({
      title: "Delete client",
      message: `Delete "${selectedClient.clientName}"? This also removes all of their committed and in-progress spend data. This cannot be undone from here.`,
    });
    if (result === null) return;
    try {
      await apiFetch(`clients/${selectedClient.clientId}`, { method: "DELETE" });
      setSelectedClient(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const deleteBatch = async (b: SpendBatch) => {
    const isCommitted = b.status === "Committed";
    const warn = isCommitted
      ? `Delete "${b.fileName}"? This removes its committed spend records from this client's data. This cannot be undone from here.`
      : `Discard "${b.fileName}"? This removes the in-progress upload.`;
    const result = await confirm({ title: isCommitted ? "Delete file" : "Discard upload", message: warn });
    if (result === null) return;
    try {
      await apiFetch(`spend/batches/${b.batchGuid}`, { method: "DELETE" });
      if (selectedClient) {
        loadBatches(selectedClient.clientId);
        if (isCommitted) loadRecords(selectedClient.clientId);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const deleteRecord = async (r: ClientSpendRecord) => {
    const result = await confirm({
      title: "Delete record",
      message: `Delete this spend record for ${r.supplierNameRaw} / ${r.tradeNameRaw}? This cannot be undone from here.`,
    });
    if (result === null) return;
    try {
      await apiFetch(`spend/records/${r.clientSpendId}`, { method: "DELETE" });
      if (selectedClient) loadRecords(selectedClient.clientId);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const selectableRecords = records.filter((r) => canManage(r.createdByAppUserId));
  const allRecordsSelected =
    selectableRecords.length > 0 && selectableRecords.every((r) => selectedRecordIds.has(r.clientSpendId));

  const toggleOneRecord = (id: number) => {
    setSelectedRecordIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllRecords = () => {
    setSelectedRecordIds((prev) => {
      if (allRecordsSelected) return new Set();
      const next = new Set(prev);
      selectableRecords.forEach((r) => next.add(r.clientSpendId));
      return next;
    });
  };

  const bulkDeleteRecords = async () => {
    const selected = records.filter((r) => selectedRecordIds.has(r.clientSpendId));
    if (!selected.length) return;

    const result = await confirm({
      title: "Delete selected records",
      message: `Delete ${selected.length} committed spend record(s)? This cannot be undone from here.`,
    });
    if (result === null) return;

    const confirmAgain = await confirm({
      title: "Are you sure?",
      message: `This cannot be undone. Confirm deleting ${selected.length} record(s)?`,
      confirmLabel: "Yes, delete",
    });
    if (confirmAgain === null) return;

    try {
      await Promise.all(
        selected.map((r) => apiFetch(`spend/records/${r.clientSpendId}`, { method: "DELETE" })),
      );
      setSelectedRecordIds(new Set());
      if (selectedClient) loadRecords(selectedClient.clientId);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const editRecord = async (r: ClientSpendRecord) => {
    const result = await editFields({
      title: `Edit spend record — ${r.supplierNameRaw} / ${r.tradeNameRaw}`,
      fields: [
        { key: "invoiceDate", label: "Invoice date", type: "date" },
        { key: "quantity", label: "Quantity", type: "number" },
        { key: "quantityUomCode", label: "Quantity UOM" },
        { key: "unitPrice", label: "Unit price", type: "number" },
        { key: "priceUomCode", label: "Price UOM" },
        { key: "currencyCode", label: "Currency" },
        { key: "poNumber", label: "PO number" },
        { key: "poLineNumber", label: "PO line number" },
        { key: "materialName", label: "Material name" },
        { key: "materialNumber", label: "Material number" },
        { key: "businessUnit", label: "Business unit" },
        { key: "notes", label: "Notes" },
      ],
      initialValues: r,
    });
    if (result === null) return;
    try {
      await apiFetch(`spend/records/${r.clientSpendId}`, {
        method: "PATCH",
        body: JSON.stringify({
          invoiceDate: result.invoiceDate,
          quantity: Number(result.quantity),
          quantityUomCode: result.quantityUomCode,
          unitPrice: Number(result.unitPrice),
          priceUomCode: result.priceUomCode,
          currencyCode: result.currencyCode,
          poNumber: result.poNumber || null,
          poLineNumber: result.poLineNumber || null,
          materialName: result.materialName || null,
          materialNumber: result.materialNumber || null,
          businessUnit: result.businessUnit || null,
          notes: result.notes || null,
        }),
      });
      if (selectedClient) loadRecords(selectedClient.clientId);
    } catch (err) {
      if (err instanceof ApiError) {
        setError((err.body as { detail?: string })?.detail ?? err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, totalCount);

  return (
    <>
    <section className="lane on">
      <h2>Clients</h2>
      <p className="lede">
        Look up a client to see and manage every file that has been uploaded on
        their behalf and every committed spend record, matching the columns on
        the original intake workbook so it can be validated and revisited at any time.
      </p>

      <div className="sec">
        <div className="card narrow">
          <label className="fl" htmlFor="portal-client-search">Client<span className="req">*</span></label>
          {selectedClient ? (
            editingClient ? (
              <div>
                <label className="fl" htmlFor="edit-client-name">Client name</label>
                <input id="edit-client-name" className="ctl" value={editName} onChange={(e) => setEditName(e.target.value)} />
                <label className="fl" style={{ marginTop: 10 }} htmlFor="edit-client-code">Client code</label>
                <input id="edit-client-code" className="ctl" value={editCode} onChange={(e) => setEditCode(e.target.value)} />
                <label className="fl" style={{ marginTop: 10 }} htmlFor="edit-client-status">Status</label>
                <select id="edit-client-status" className="ctl" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                  {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button className="btn" disabled={savingClient || !editName.trim()} onClick={saveClient}>
                    <span className="material-symbols-sharp">check</span>{savingClient ? "Saving…" : "Save"}
                  </button>
                  <button className="btn ghost" disabled={savingClient} onClick={() => setEditingClient(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="chip ok" style={{ fontSize: 14, padding: "6px 12px" }}>
                <span className="material-symbols-sharp">check</span>
                {selectedClient.clientName}
                {selectedClient.clientCode && <span style={{ color: "var(--grey)" }}>({selectedClient.clientCode})</span>}
                {appRole === "Admin" && (
                  <>
                    <button className="linkbtn" style={{ marginLeft: 8 }} onClick={startEditClient}>
                      Edit
                    </button>
                    <button className="linkbtn" style={{ marginLeft: 8, color: "var(--salmon)" }} onClick={deleteClient}>
                      Delete
                    </button>
                  </>
                )}
                <button
                  className="linkbtn"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setSelectedClient(null);
                    setBatches([]);
                    setRecords([]);
                    setTotalCount(0);
                    setOffset(0);
                  }}
                >
                  Change
                </button>
              </div>
            )
          ) : (
            <ClientCombobox id="portal-client-search" selected={selectedClient} onSelect={setSelectedClient} />
          )}
        </div>
      </div>

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      {selectedClient && (
        <>
          <div className="sec">
            <div className="sec-hd"><h3>Uploaded Files</h3></div>
            <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Committed</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batchesLoading && (
                    <tr><td colSpan={5}>Loading…</td></tr>
                  )}
                  {!batchesLoading && batches.length === 0 && (
                    <tr><td colSpan={5}>No files uploaded yet for this client.</td></tr>
                  )}
                  {batches.map((b) => (
                    <tr key={b.batchGuid}>
                      <td>{b.fileName}<div style={{ color: "var(--grey)", fontSize: 11.5 }}>{formatBytes(b.fileSizeBytes)}</div></td>
                      <td><span className={`chip ${STATUS_CHIP[b.status]}`}>{b.status}</span></td>
                      <td>{new Date(b.uploadedUtc).toLocaleString()}</td>
                      <td>{b.committedUtc ? new Date(b.committedUtc).toLocaleString() : "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="btn ghost" onClick={() => downloadFile(b.batchGuid)}>
                          <span className="material-symbols-sharp">download</span>Download
                        </button>
                        {REVIEWABLE_STATUSES.includes(b.status) && (
                          <button className="btn ghost" style={{ marginLeft: 6 }} onClick={() => navigate(`${PATHS.intakeLane("spend")}?resume=${b.batchGuid}`)}>
                            <span className="material-symbols-sharp">rate_review</span>Continue Reviewing
                          </button>
                        )}
                        {b.status !== "Discarded" && (
                          <button
                            className="btn ghost"
                            style={{ marginLeft: 6 }}
                            disabled={!canManage(b.createdByAppUserId)}
                            title={!canManage(b.createdByAppUserId) ? "You can only delete data you uploaded, unless you're an Admin" : undefined}
                            onClick={() => deleteBatch(b)}
                          >
                            <span className="material-symbols-sharp">delete</span>Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sec">
            <div className="sec-hd">
              <h3>Spend Records</h3>
              <span className="note">{totalCount} total</span>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="inline">
                <input
                  className="ctl" placeholder="Filter by supplier name…"
                  value={supplierFilter}
                  onChange={(e) => { setSupplierFilter(e.target.value); setOffset(0); }}
                />
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
                  Invoice date from
                  <input
                    className="ctl" type="date" value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
                  Invoice date to
                  <input
                    className="ctl" type="date" value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                  />
                </label>
              </div>
            </div>

            {selectedRecordIds.size > 0 && (
              <div className="card" style={{ padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>{selectedRecordIds.size} selected</span>
                <button className="btn danger" onClick={bulkDeleteRecords}>
                  <span className="material-symbols-sharp">delete_forever</span>Delete Selected
                </button>
              </div>
            )}

            <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
              <table style={{ minWidth: 1700 }}>
                <thead>
                  <tr>
                    <th style={{ width: 1 }}>
                      <input type="checkbox" checked={allRecordsSelected} onChange={toggleAllRecords} disabled={selectableRecords.length === 0} />
                    </th>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.label}
                        style={c.sortKey ? { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" } : { whiteSpace: "nowrap" }}
                        onClick={c.sortKey ? () => toggleSort(c.sortKey!) : undefined}
                      >
                        {c.label}
                        {c.sortKey && sortBy === c.sortKey && (
                          <span className="material-symbols-sharp" style={{ fontSize: 15, verticalAlign: "-3px" }}>
                            {sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
                          </span>
                        )}
                      </th>
                    ))}
                    <th>Source file</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recordsLoading && (
                    <tr><td colSpan={COLUMNS.length + 3}>Loading…</td></tr>
                  )}
                  {!recordsLoading && records.length === 0 && (
                    <tr><td colSpan={COLUMNS.length + 3}>No committed spend records match these filters.</td></tr>
                  )}
                  {records.map((r) => (
                    <tr key={r.clientSpendId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRecordIds.has(r.clientSpendId)}
                          disabled={!canManage(r.createdByAppUserId)}
                          onChange={() => toggleOneRecord(r.clientSpendId)}
                        />
                      </td>
                      {COLUMNS.map((c) => (
                        <td key={c.label} style={{ whiteSpace: "nowrap" }}>{c.render(r)}</td>
                      ))}
                      <td>
                        <button className="linkbtn" onClick={() => downloadFile(r.batchGuid)}>
                          {r.batchGuid.slice(0, 8)}
                        </button>
                      </td>
                      <td style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.createdByAppUserId)}
                          title={!canManage(r.createdByAppUserId) ? "You can only edit data you uploaded, unless you're an Admin" : undefined}
                          onClick={() => editRecord(r)}
                        >
                          <span className="material-symbols-sharp">edit</span>
                        </button>
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.createdByAppUserId)}
                          title={!canManage(r.createdByAppUserId) ? "You can only delete data you uploaded, unless you're an Admin" : undefined}
                          onClick={() => deleteRecord(r)}
                        >
                          <span className="material-symbols-sharp">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalCount > 0 && (
              <div className="gate">
                <span className="msg">Showing {pageStart}–{pageEnd} of {totalCount}</span>
                <button className="btn ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  <span className="material-symbols-sharp">chevron_left</span>Previous
                </button>
                <button className="btn ghost" disabled={pageEnd >= totalCount} onClick={() => setOffset(offset + PAGE_SIZE)}>
                  Next<span className="material-symbols-sharp">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
    {dialog}
    {editDialog}
    </>
  );
}
